"use client";

import { ReactNode, useState } from "react";
import ExpandIcon from "@/assets/icons/chevron-down.svg?react";
import RenderPrice from "@/components/productPrice/RenderPrice";
import { cn } from "@/lib/tailwind";
import type { OrderSummaryDiscountLine } from "@/utils/orderSummaryPrices";

type OrderSummaryTotalsVariant = "cart" | "checkout";

interface OrderSummaryTotalsProps {
  lang: string;
  currency: string;
  staticText?: Record<string, string>;
  variant?: OrderSummaryTotalsVariant;
  subtotalAmount: number;
  giftWrapEnabled?: boolean;
  giftWrapAmount?: number;
  shippingAmount?: number;
  showShippingAmount?: boolean;
  cashOnDeliveryEnabled?: boolean;
  cashOnDeliveryAmount?: number;
  cashOnDeliveryLabel?: string;
  discountLines?: OrderSummaryDiscountLine[];
  totalDiscountAmount?: number;
  orderTotalAmount: number;
  vatAmount?: number;
  showVat?: boolean;
}

const rowClassName = "flex items-center gap-2 border-b border-tertiary-8 py-4 text-sm";

const ChevronSlot = ({ children }: { children?: ReactNode }) => (
  <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center">{children}</span>
);

const SummaryRow = ({
  label,
  value,
  valueClassName,
  labelClassName,
  indented,
}: {
  label: ReactNode;
  value: ReactNode;
  valueClassName?: string;
  labelClassName?: string;
  indented?: boolean;
}) => (
  <div className={rowClassName}>
    <span className={cn("min-w-0 flex-1", indented && "ps-4", labelClassName)}>{label}</span>
    <span className={cn("shrink-0 font-bold tabular-nums", valueClassName)}>{value}</span>
    <ChevronSlot />
  </div>
);

const OrderSummaryTotals = ({
  lang,
  currency,
  staticText,
  variant = "checkout",
  subtotalAmount,
  giftWrapEnabled,
  giftWrapAmount = 0,
  shippingAmount = 0,
  showShippingAmount = true,
  cashOnDeliveryEnabled,
  cashOnDeliveryAmount = 0,
  cashOnDeliveryLabel,
  discountLines = [],
  totalDiscountAmount,
  orderTotalAmount,
  vatAmount = 0,
  showVat = false,
}: OrderSummaryTotalsProps) => {
  const [isDiscountOpen, setIsDiscountOpen] = useState(false);
  const discountTotal = totalDiscountAmount ?? 0;
  const hasDiscounts = discountLines.length > 0 && discountTotal > 0.01;

  const renderPrice = (amount: number, isNegative = false) => (
    <RenderPrice price={amount} currencyCode={currency} lang={lang} isNegative={isNegative} />
  );

  const renderFreeOrPrice = (amount: number) =>
    amount > 0 ? renderPrice(amount) : staticText?.cart_free_label;

  return (
    <div>
      <SummaryRow
        label={staticText?.cart_subtotal_label}
        value={renderPrice(subtotalAmount)}
      />

      {giftWrapEnabled && (
        <SummaryRow
          label={staticText?.cart_gift_packaging_label}
          value={renderFreeOrPrice(giftWrapAmount)}
          valueClassName={giftWrapAmount > 0 ? "" : "text-success"}
        />
      )}

      <SummaryRow
        label={staticText?.cart_shipping_label}
        value={
          variant === "cart" || !showShippingAmount ? (
            <span className="text-[13px] font-normal">
              {staticText?.cart_shipping_calculated_at_checkout_label}
            </span>
          ) : (
            renderFreeOrPrice(shippingAmount)
          )
        }
        valueClassName={
          variant !== "cart" && showShippingAmount && shippingAmount <= 0 ? "text-success" : ""
        }
      />

      {cashOnDeliveryEnabled && (
        <SummaryRow
          label={
            cashOnDeliveryLabel ||
            staticText?.payment_details_cash_on_delivery_radio_button_label
          }
          value={renderFreeOrPrice(cashOnDeliveryAmount)}
          valueClassName={cashOnDeliveryAmount > 0 ? "" : "text-success"}
        />
      )}

      {hasDiscounts && (
        <>
          <button
            type="button"
            className={cn(rowClassName, "w-full text-start")}
            onClick={() => setIsDiscountOpen((open) => !open)}
            aria-expanded={isDiscountOpen}
          >
            <span className="min-w-0 flex-1">
              {staticText?.cart_discount_amount_label}
            </span>
            <span className="shrink-0 font-bold tabular-nums">
              {renderPrice(discountTotal, true)}
            </span>
            <ChevronSlot>
              <ExpandIcon
                className={cn("h-full w-full transition-transform", isDiscountOpen && "rotate-180")}
              />
            </ChevronSlot>
          </button>
          {isDiscountOpen &&
            discountLines.map((line, index) => (
              <SummaryRow
                key={`${line.type ?? line.name}-${index}`}
                label={line.name}
                value={renderPrice(line.amount, true)}
                indented
              />
            ))}
        </>
      )}

      <div className="py-4 text-sm">
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 font-semibold">
            {staticText?.cart_order_total_label}
          </p>
          <p className="shrink-0 font-bold tabular-nums">{renderPrice(orderTotalAmount)}</p>
          <ChevronSlot />
        </div>
        {showVat && vatAmount > 0 && (
          <div className="mt-1 flex items-center gap-2">
            <p className="min-w-0 flex-1">{staticText?.cart_includes_vat_label}</p>
            <p className="shrink-0 font-bold tabular-nums">{renderPrice(vatAmount)}</p>
            <ChevronSlot />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderSummaryTotals;
